import { create } from 'zustand';

/** 장바구니에서 해제해 둔 항목 — 새로고침·다른 화면 다녀와도 유지(CART-11) */
const UNCHECKED_STORAGE_KEY = 'indrive.v2.cart.unchecked';

const readUnchecked = (): number[] => {
  try {
    const raw = window.localStorage.getItem(UNCHECKED_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id): id is number => typeof id === 'number')
      : [];
  } catch {
    return [];
  }
};

const writeUnchecked = (ids: number[]) => {
  try {
    window.localStorage.setItem(UNCHECKED_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // 저장소를 못 쓰는 환경(사생활 보호 모드 등)이면 이 탭 안에서만 유지된다
  }
};

type CartDownloadState = {
  /** 진행 중인 ZIP 작업 (N13/N15) — 화면을 옮겨도 전역 감시자가 이어서 조회한다 */
  jobId: string | null;
  /** ZIP 요청을 보냈고 아직 작업 id를 받기 전 (응답까지 1~1.5초) */
  requesting: boolean;
  /** 완료 시 장바구니에서 뺄 파일 id (권한 제외분은 빠져 있다 · 제외 id를 모르면 비어 있다) */
  removeOnDone: number[];
  /** 사용자가 선택 해제한 장바구니 항목 — 나머지(새로 담긴 것 포함)는 기본 선택 */
  uncheckedIds: number[];
  setRequesting: (requesting: boolean) => void;
  startJob: (jobId: string, removeOnDone: number[]) => void;
  finishJob: () => void;
  setUnchecked: (ids: number[]) => void;
};

/**
 * ZIP을 만드는 중인지 — 요청을 보낸 순간부터 작업이 끝날 때까지.
 * 이탈 경고(화면 이동 확인 창 · 새로고침·탭 닫기)가 이 값을 함께 본다(CART-21 —
 * 작업 id만 보면 요청 응답 전 1~1.5초 동안 경고 없이 떠날 수 있었다).
 */
export const selectZipBusy = (state: CartDownloadState) =>
  state.requesting || state.jobId != null;

/**
 * 장바구니 다운로드 전역 상태 (Zustand).
 *
 * ZIP 작업 id를 컴포넌트 state에 두면 장바구니를 벗어나는 순간 폴링이 끊겨 ZIP을 못 받았다
 * (CART-21). 작업은 여기 두고 v2 레이아웃의 `CartDownloadWatcher`가 끝까지 조회해 받는다.
 * 선택 해제 목록도 여기서 localStorage와 함께 들고 있어 새로고침 뒤에도 유지된다.
 */
export const useCartDownloadStore = create<CartDownloadState>((set) => ({
  jobId: null,
  requesting: false,
  removeOnDone: [],
  uncheckedIds: readUnchecked(),
  setRequesting: (requesting) => set({ requesting }),
  startJob: (jobId, removeOnDone) => set({ jobId, removeOnDone }),
  finishJob: () => set({ jobId: null, removeOnDone: [] }),
  setUnchecked: (ids) => {
    writeUnchecked(ids);
    set({ uncheckedIds: ids });
  },
}));
